/**
 * StudySync Phone Verification Component
 * Handles SMS verification for participants
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Phone,
  Sms,
  CheckCircle,
  Error,
  Refresh,
} from '@mui/icons-material';
import { VerificationService } from '../../services/verificationService';
import type { VerificationStatus } from '../../services/verificationService';

interface PhoneVerificationProps {
  participantId: string;
  phoneNumber: string;
  studyCode: string;
  studyName: string;
  onVerificationComplete: (status: VerificationStatus) => void;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  participantId,
  phoneNumber,
  studyCode,
  studyName,
  onVerificationComplete
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const cooldownTime = 30; // seconds

  // Handle resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = async () => {
    setSendingCode(true);
    setError(null);
    setSuccess(null);

    try {
      await VerificationService.sendPhoneVerificationCode({
        participantId,
        phoneNumber,
        studyCode
      });

      setCodeSent(true);
      setSuccess('Verification code sent to your phone!');
      setResendCooldown(cooldownTime);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (!VerificationService.validateVerificationCode(verificationCode)) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await VerificationService.verifyPhoneCode({
        participantId,
        phoneNumber,
        studyCode,
        verificationCode
      });

      if (response.success && response.data) {
        setSuccess('Phone verification successful!');
        onVerificationComplete({
          participantId,
          email: '', // Not needed for phone verification
          studyCode,
          verificationStatus: response.data.verificationStatus as 'pending' | 'email_verified' | 'phone_verified' | 'fully_verified' | 'failed',
          phoneVerifiedAt: new Date().toISOString()
        });
      } else {
        setError(response.error || 'Verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setVerificationCode('');
    setError(null);
    setSuccess(null);
    await handleSendCode();
  };

  const formatPhoneNumber = (phone: string) => {
    // Format phone number for display
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const isCodeValid = VerificationService.validateVerificationCode(verificationCode);
  const canResend = resendCooldown === 0 && !sendingCode;

  return (
    <Card elevation={3} sx={{ maxWidth: 500, mx: 'auto' }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Phone sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Phone Verification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll send a verification code to your phone number
          </Typography>
          <Chip
            icon={<Phone />}
            label={formatPhoneNumber(phoneNumber)}
            color="primary"
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </Box>

        {!codeSent ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Click the button below to receive a verification code via SMS.
            </Typography>
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSendCode}
              disabled={sendingCode}
              startIcon={sendingCode ? <CircularProgress size={20} /> : <Sms />}
              sx={{ mb: 2 }}
            >
              {sendingCode ? 'Sending Code...' : 'Send Verification Code'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the 6-digit code sent to your phone:
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2em' }
              }}
              sx={{ mb: 3 }}
              error={verificationCode.length === 6 && !isCodeValid}
              helperText={
                verificationCode.length === 6 && !isCodeValid
                  ? 'Please enter a valid 6-digit code'
                  : ''
              }
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleVerifyCode}
              disabled={loading || !isCodeValid}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              sx={{ mb: 2 }}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?
              </Typography>
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={!canResend}
                startIcon={<Refresh />}
                size="small"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </Button>
            </Box>

          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} icon={<Error />}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircle />}>
            {success}
          </Alert>
        )}

      </CardContent>
    </Card>
  );
};

export default PhoneVerification;
